// frontend/src/pages/asset-admin/AssetAdminAssets.js
/**
 * Asset Admin - Assets Management Page
 * 
 * Full CRUD operations for assets with:
 * - Searchable, filterable asset table
 * - Assign/Return modals with photo upload
 * - Asset details view
 * 
 * Theme: Red (Admin)
 */

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Pagination } from '../../components/UI/Pagination';
import { useDebounce } from '../../hooks/useDebounce';
import { useSearchParams } from 'react-router-dom';
import toast from '../../utils/toast';
import {
  Package, Search, Plus, SlidersHorizontal, Eye, UserPlus, UserMinus, Edit, 
  Laptop, Key, Car, FileKey, ChevronDown, X, Camera, Upload,
  AlertCircle, CheckCircle, Clock, AlertTriangle, CreditCard, Fuel, DoorOpen, ScanLine, Smartphone,
  Grid3X3, List, User, FileText, Download, Loader2, FileSpreadsheet, Table2,
  ShieldCheck, ShieldOff, MapPin
} from 'lucide-react';
import Portal from '../../components/UI/Portal';
import Modal from '../../components/UI/Modal';
import FormSelect from '../../components/UI/FormSelect';
import DebouncedSearchInput from '../../components/UI/DebouncedSearchInput';
import LetterPreviewModal from '../../components/LetterPreviewModal';
import EditAssetModal from '../../components/EditAssetModal';
import AddAssetModal from '../../components/AddAssetModal';
import DeactivateAccessModal from '../../components/asset/DeactivateAccessModal';
import ReturnToLessorModal, { ReturnToLessorButton } from '../../components/asset/ReturnToLessorModal';
import AssetAssigneeCombobox from '../../components/asset/AssetAssigneeCombobox';
import ReturnToInventoryModal from '../../components/asset/ReturnToInventoryModal';
import { useTabPersistence, usePersistence } from '../../hooks/usePersistence';
import { getDepartmentColor } from '../../config/departmentConfig';
import {
  useAssets,
  useAssetCategories,
  useAssignAsset,
  useBulkAssignAssets,
  useReturnToInventory,
  useDeactivateAccess,
  useReturnToLessor,
} from '../../hooks/useAssetInventoryData';
import { useDepartments } from '../../hooks/useConfigData';
// LoadingSpinner removed - using skeleton screens per design system
import { getCategoryColorByCode } from '../../utils/assetStatusColors';
import { StatusBadge } from '../../components/UI/StatusBadge';
import { OwnershipBadge } from '../../components/UI/OwnershipBadge';
import { InfoBadge } from '../../components/UI/InfoBadge';
import { deviceLineContext, deviceLineLabel, isDeviceMobileLine, showsRegisterHolder } from '../../utils/mobileLineDisplay';
import { FilterButton } from '../../components/UI/FilterButton';
import PillToggle from '../../components/UI/PillToggle';
import { getTodayInSingaporeTimezone } from '../../utils/dateUtils';
import {
  canDeactivateAccess,
  canReturnAssetToInventory,
  canAssignAsset,
  requiresPhysicalHandover,
} from '../../utils/assetAccess';
import { useAssetDetail } from '../../contexts/AssetDetailContext';
import { buildAssetQueryParams } from '../../utils/assetQueryParams';
import { assetInventoryAPI } from '../../services/api';
import { exportAssetsPDF, exportAssetsXLSX, exportAssetsCSV } from '../../utils/assetExportUtils';
import { OWNERSHIP_OPTIONS } from '../../constants/assetOptions';

// ============================================
// CONSTANTS
// ============================================
const VALID_TABS = ['all', 'available', 'assigned', 'deployed', 'in_repair', 'pending_disposal'];
const PAGE_SIZE = 25;

// Multi-select filters are persisted in the URL (source of truth) so they
// survive refresh, can be bookmarked/shared, and work with browser back/forward.
// These are the canonical query-string keys the API builder also consumes.
const FILTER_URL_KEYS = {
  categories: 'category__code__in',
  statuses: 'status__in',
  departments: 'department__in',
  ownershipTypes: 'ownership_type__in',
};

const parseCsvParam = (params, key) =>
  (params.get(key) || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

const parseFiltersFromParams = (params) => ({
  categories: parseCsvParam(params, FILTER_URL_KEYS.categories),
  statuses: parseCsvParam(params, FILTER_URL_KEYS.statuses),
  departments: parseCsvParam(params, FILTER_URL_KEYS.departments),
  ownershipTypes: parseCsvParam(params, FILTER_URL_KEYS.ownershipTypes),
});

const EMPTY_FILTERS = {
  categories: [],
  statuses: [],
  departments: [],
  ownershipTypes: [],
};

// Category options are now fetched dynamically from API
// Icon mapping for category codes (used for display only)
const CATEGORY_ICON_MAP = {
  IT: Laptop,
  ACC: DoorOpen,
  FLT: Car,
  LIC: FileKey,
  OFF: Package,
  FUEL: CreditCard,
  DSL: Fuel,
  TEL: Smartphone,
};

const STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'deployed', label: 'Deployed' },
  { value: 'active', label: 'Active' },
  { value: 'in_repair', label: 'In Repair' },
  { value: 'at_workshop', label: 'At Workshop' },
  { value: 'pending_disposal', label: 'Pending Disposal' },
  { value: 'scrapped', label: 'Scrapped' },
  { value: 'sold', label: 'Sold' },
  { value: 'written_off', label: 'Written Off' },
  { value: 'disposed', label: 'Disposed' },
  { value: 'terminated', label: 'Terminated' },
  { value: 'returned_to_lessor', label: 'Returned to Lessor' },
  { value: 'deactivated', label: 'Deactivated' },
  { value: 'lost', label: 'Lost/Missing' },
];

// Department options will be fetched dynamically from API

// ============================================
// CATEGORY ICON COMPONENT
// ============================================
const CategoryIcon = ({ code, className = "w-5 h-5" }) => {
  const Icon = CATEGORY_ICON_MAP[code] || Package;
  return <Icon className={className} />;
};

// ============================================
// TREND MICRO ENDPOINT-PROTECTION BADGE
// ============================================
// Compact shield shown for IT endpoints whose Trend Micro Apex One status has
// been synced (via the import_trendmicro_agents command). Renders nothing when
// the asset has no recorded status, so non-endpoints stay clean.
const TrendMicroBadge = ({ asset, className = "w-4 h-4" }) => {
  const tm = asset?.trendmicro;
  if (!tm) return null;
  if (tm.installed) {
    const installedOn = (() => {
      if (!tm.install_date) return '';
      const d = new Date(tm.install_date);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    })();
    return (
      <span title={`Trend Micro agent installed${installedOn ? ` (since ${installedOn})` : ''}`}>
        <ShieldCheck className={`${className} text-green-600`} aria-label="Endpoint protected" />
      </span>
    );
  }
  return (
    <span title="No Trend Micro agent detected on this endpoint">
      <ShieldOff className={`${className} text-red-500`} aria-label="Endpoint not protected" />
    </span>
  );
};

// ============================================
// FILTER CHECKBOX COMPONENT (Reusable)
// ============================================
const FilterCheckbox = ({ label, checked, onChange, icon: Icon }) => (
  <label className="flex items-center gap-2 cursor-pointer group py-1.5">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="w-4 h-4 rounded border-gray-300 accent-red-600 text-red-600 
                 focus-visible:ring-red-500 focus-visible:ring-offset-0 cursor-pointer
                 transition-colors"
    />
    {Icon && <Icon className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />}
    <span className="text-sm text-gray-700 group-hover:text-gray-900 select-none">
      {label}
    </span>
  </label>
);


// ============================================
// EXPORT DROPDOWN (rendered via Portal to escape overflow-hidden)
// ============================================
const ExportDropdownMenu = ({ anchorRef, dropdownRef, onExport }) => {
  const [style, setStyle] = useState({});

  useEffect(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
      zIndex: 9999,
    });
  }, [anchorRef]);

  return (
    <Portal>
      <div
        ref={dropdownRef}
        style={style}
        className="bg-white rounded-xl shadow-lg border border-gray-200 py-1 min-w-[180px]"
      >
        <button
          type="button"
          onClick={() => onExport('pdf')}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />
          <span>PDF Document</span>
        </button>
        <button
          type="button"
          onClick={() => onExport('xlsx')}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <FileSpreadsheet className="w-4 h-4 text-green-600 flex-shrink-0" />
          <span>Excel Spreadsheet</span>
        </button>
        <button
          type="button"
          onClick={() => onExport('csv')}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <Table2 className="w-4 h-4 text-blue-500 flex-shrink-0" />
          <span>CSV File</span>
        </button>
      </div>
    </Portal>
  );
};

// The plan often already starts with the telco ("Singtel 5G+…"). Showing
// brand and model together would read "Singtel Singtel 5G+…".
const assetPlanLine = (asset) => {
  const brand = (asset?.brand || '').trim();
  const model = (asset?.model || '').trim();
  if (brand && model.toLowerCase().startsWith(brand.toLowerCase())) return model;
  return [brand, model].filter(Boolean).join(' ');
};

const assetDisplayName = (asset) => asset?.display_name || asset?.asset_tag || 'Asset';

const registerHolderNote = (asset) => (
  asset?.register_holder_kind === 'equipment'
    ? 'Site or device'
    : 'Not linked to a staff record'
);

// ============================================
// VIEW MODE HELPERS
// ============================================
const getDefaultViewMode = () => {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('asset-admin-assets-viewMode');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed === 'grid' || parsed === 'list') return parsed;
      } catch (e) { /* use default */ }
    }
    return window.innerWidth < 768 ? 'grid' : 'list';
  }
  return 'grid';
};

// ============================================
// ADMIN ASSET CARD (Grid View)
// ============================================
const AdminAssetCard = ({ asset, onViewDetails, onAssign, onReturn, onReturnToLessor, onEdit, onViewLetter, onDeactivateAccess }) => {
  const colors = getCategoryColorByCode(asset.category_code);

  const dueInfo = (() => {
    if (!asset.next_due_date) return null;
    const dueDate = new Date(asset.next_due_date);
    const days = Math.ceil((dueDate - new Date()) / (1000 * 60 * 60 * 24));
    const isOverdue = days < 0;
    const isUrgent = days >= 0 && days <= 30;
    const isWarning = days > 30 && days <= 90;
    return { dueDate, days, isOverdue, isUrgent, isWarning };
  })();

  return (
    <div className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow flex flex-col h-full gap-3">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 ${colors.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
            <CategoryIcon code={asset.category_code} className={`w-5 h-5 ${colors.text}`} />
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 flex items-center gap-1.5">
              {assetDisplayName(asset)}
              {isDeviceMobileLine(asset) && (
                <InfoBadge color="blue" size="xs" label="Device" title={deviceLineLabel(asset)} />
              )}
              <OwnershipBadge ownershipType={asset.ownership_type} />
              <TrendMicroBadge asset={asset} />
            </h4>
            <p className="text-sm text-gray-600">{asset.asset_type?.name || asset.asset_type_name}</p>
            {(asset.brand || asset.model) && (
              <p className="text-xs text-gray-500 mt-0.5">{assetPlanLine(asset)}</p>
            )}
          </div>
        </div>
        <StatusBadge status={asset.status} />
      </div>

      <div className="ml-[52px] space-y-1 flex-1">
        {asset.serial_number && (
          <p className="text-xs text-gray-500">S/N: {asset.serial_number}</p>
        )}

        {asset.current_assignee_name ? (
          <div className="flex items-center gap-1 text-xs text-gray-600">
            <User className="w-3 h-3" />
            <span>{asset.current_assignee_name}</span>
            <span className="text-gray-400">({asset.current_assignee_staff_id})</span>
          </div>
        ) : showsRegisterHolder(asset) ? (
          <div className="flex items-center gap-1 text-xs text-gray-600">
            <User className="w-3 h-3" />
            <span>{asset.register_holder}</span>
            <span className="text-gray-400">({registerHolderNote(asset)})</span>
          </div>
        ) : asset.deployed_project_name ? (
          <div className="flex items-center gap-1 text-xs text-blue-600">
            <Package className="w-3 h-3" />
            <span>{asset.deployed_project_name}</span>
          </div>
        ) : null}

        {isDeviceMobileLine(asset) && (
          <div className="flex items-center gap-1 text-xs text-blue-700">
            <MapPin className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{deviceLineLabel(asset)}</span>
          </div>
        )}

        {dueInfo && (
          <div className={`flex items-center gap-1 text-xs font-medium ${
            dueInfo.isOverdue ? 'text-red-600' :
            dueInfo.isUrgent ? 'text-amber-600' :
            dueInfo.isWarning ? 'text-yellow-600' : 'text-gray-500'
          }`}>
            {dueInfo.isOverdue ? <AlertCircle className="w-3 h-3" /> :
             dueInfo.isUrgent ? <AlertTriangle className="w-3 h-3" /> :
             <Clock className="w-3 h-3" />}
            <span>
              {dueInfo.dueDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
            <span className="font-normal text-gray-400">· {asset.next_due_type}</span>
          </div>
        )}
      </div>

      <div className="card-action-footer">
        <button onClick={() => onViewDetails(asset)} className="icon-button-admin" title="View Details">
          <Eye className="w-4 h-4" />
        </button>
        {asset.ack_letter_id && (
          <button
            onClick={() => onViewLetter(asset)}
            className="icon-button-admin text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            title={`View acknowledgement letter${asset.ack_letter_reference ? ` (${asset.ack_letter_reference})` : ''}`}
          >
            <FileText className="w-4 h-4" />
          </button>
        )}
        {canAssignAsset(asset) && (
          <button
            onClick={() => onAssign(asset)}
            className="icon-button-admin text-green-600 hover:text-green-700 hover:bg-green-50"
            title="Assign"
          >
            <UserPlus className="w-4 h-4" />
          </button>
        )}
        {asset.status === 'assigned' && canReturnAssetToInventory(asset) && (
          <button
            onClick={() => onReturn(asset)}
            className="icon-button-admin text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
            title="Return to Inventory"
          >
            <UserMinus className="w-4 h-4" />
          </button>
        )}
        <ReturnToLessorButton asset={asset} onClick={onReturnToLessor} />
        {canDeactivateAccess(asset) && (
          <button
            onClick={() => onDeactivateAccess(asset)}
            className="icon-button-danger"
            title="Deactivate access"
            aria-label="Deactivate access"
          >
            <ShieldOff className="w-4 h-4" />
          </button>
        )}
        {asset.can_edit !== false && (
          <button onClick={() => onEdit(asset)} className="icon-button-admin" title="Edit">
            <Edit className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};


// ============================================
// ASSIGN MODAL
// ============================================
const AssignModal = ({ isOpen, onClose, asset, onSubmit, isLoading }) => {
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [condition, setCondition] = useState('good');
  const [notes, setNotes] = useState('');
  const isPhysicalHandover = requiresPhysicalHandover(asset);

  useEffect(() => {
    if (!isOpen) {
      setSelectedEmployee('');
      setCondition('good');
      setNotes('');
    }
  }, [isOpen]);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedEmployee) return;
    onSubmit({
      asset_id: asset.id,
      employee_id: selectedEmployee,
      effective_date: getTodayInSingaporeTimezone(),
      condition: isPhysicalHandover ? condition : undefined,
      notes,
    });
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Assign Asset"
      size="sm"
      theme="admin"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-admin-outline">
            Cancel
          </button>
          <button
            type="submit"
            form="assign-modal-form"
            disabled={!selectedEmployee || isLoading}
            className="btn btn-admin-primary"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            {isLoading ? 'Assigning...' : 'Assign Asset'}
          </button>
        </>
      }
    >
      <form id="assign-modal-form" onSubmit={handleSubmit} className="space-y-4">
              {/* Asset Info */}
              <div className="bg-red-50 rounded-lg p-3">
                <p className="text-sm font-medium text-red-800">{assetDisplayName(asset)}</p>
                <p className="text-xs text-red-600">{asset?.asset_type?.name || asset?.asset_type_name}</p>
              </div>

              {asset?.asset_type?.requires_recipient_receipt && (
                <p className="text-sm text-amber-800 bg-amber-50 rounded-lg p-2">
                  This asset type requires the employee to acknowledge receipt. The asset will appear in their My Assets after they acknowledge.
                </p>
              )}
              
              <div>
                <label className="form-label">
                  Assign to <span className="text-red-500">*</span>
                </label>
                <AssetAssigneeCombobox
                  value={selectedEmployee}
                  onChange={setSelectedEmployee}
                  theme="admin"
                  disabled={isLoading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Inactive and offboarded people are not listed — they cannot receive new assets.
                </p>
              </div>
              
              {isPhysicalHandover ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
                  <FormSelect
                    value={condition}
                    onChange={setCondition}
                    options={[
                      { value: 'new', label: 'New' },
                      { value: 'good', label: 'Good' },
                      { value: 'fair', label: 'Fair' },
                      { value: 'poor', label: 'Poor' },
                    ]}
                    theme="admin"
                  />
                </div>
              ) : (
                <div className="rounded-lg bg-purple-50 p-3 text-sm text-purple-800">
                  Digital entitlement — assignment records access ownership; physical
                  condition does not apply.
                </div>
              )}
              
              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional notes..."
                  className="input-admin w-full"
                />
              </div>
      </form>
    </Modal>
  );
};

// ============================================
// QR TAG PARSER (aligned with AssetQRScannerModal)
// ============================================
function parseTagFromScan(raw) {
  if (!raw || typeof raw !== 'string') return null;
  let s = raw.trim();
  const fromUrl = s.match(/[?&]tag=([^&]+)/);
  if (fromUrl) {
    try { s = decodeURIComponent(fromUrl[1]).trim(); } catch { return null; }
  }
  if (s.length < 1 || s.length > 50) return null;
  if (!/^[A-Za-z0-9_-]+$/.test(s)) return null;
  return s;
}

// ============================================
// BULK ASSIGN MODAL (with QR scanner)
// ============================================
const BULK_SCANNER_DIV = 'bulk-assign-qr-scanner';

const BulkAssignModal = ({ isOpen, onClose, onSubmit, isLoading }) => {
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [notes, setNotes] = useState('');
  const [assetSearch, setAssetSearch] = useState('');
  const [selectedAssets, setSelectedAssets] = useState([]);

  // QR scanner state
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanLookup, setScanLookup] = useState(false);
  const [scanSuccess, setScanSuccess] = useState(false);
  const scannerRef = React.useRef(null);
  const startedRef = React.useRef(false);
  const busyRef = React.useRef(false);
  const lastScannedTagRef = React.useRef(null);
  const selectedIdsRef = React.useRef(new Set());

  // Keep selectedIdsRef in sync so handleQRDecode never reads a stale closure
  const debouncedAssetSearch = useDebounce(assetSearch, 300);

  const { data: assetsData, isLoading: loadingAssets } = useAssets({
    status: 'available',
    search: debouncedAssetSearch.trim().length >= 2 ? debouncedAssetSearch : undefined,
    page_size: 20,
  });
  const availableAssets = (assetsData?.results || assetsData || []).filter(
    (a) => !selectedAssets.some((s) => s.id === a.id)
  );

  // Keep selectedIdsRef in sync so handleQRDecode never reads a stale closure
  useEffect(() => {
    selectedIdsRef.current = new Set(selectedAssets.map((a) => a.id));
  }, [selectedAssets]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setSelectedEmployee('');
      setAssetSearch('');
      setSelectedAssets([]);
      setNotes('');
      setScannerOpen(false);
      setScanSuccess(false);
      lastScannedTagRef.current = null;
      selectedIdsRef.current = new Set();
      stopScanner();
    }
  }, [isOpen]);

  // --- QR Scanner helpers ---
  const stopScanner = async () => {
    if (scannerRef.current && startedRef.current) {
      try { await scannerRef.current.stop(); } catch (_) {}
      scannerRef.current = null;
      startedRef.current = false;
    }
  };

  const startScanner = () => {
    setScannerOpen(true);
    // Delay to let the div mount
    setTimeout(async () => {
      const div = document.getElementById(BULK_SCANNER_DIV);
      if (!div) return;
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        const html5 = new Html5Qrcode(BULK_SCANNER_DIV);
        scannerRef.current = html5;
        await html5.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          handleQRDecode,
          () => {}
        );
        startedRef.current = true;
      } catch {
        toast.error('Camera unavailable. Use the search bar to add assets.');
        setScannerOpen(false);
      }
    }, 100);
  };

  const handleQRDecode = async (decodedText) => {
    if (busyRef.current) return;
    const tag = parseTagFromScan(decodedText);
    if (!tag) { toast.error('Unrecognised QR format'); return; }

    // Block re-detection of the same tag while it remains in the camera frame
    if (tag === lastScannedTagRef.current) return;

    busyRef.current = true;
    lastScannedTagRef.current = tag;
    setScanLookup(true);
    try {
      const { data: asset, status } = await import('../../services/api').then(
        (mod) => mod.assetInventoryAPI.lookupByTag(tag)
      ).then(
        (r) => ({ data: r.data, status: r.status }),
        (e) => ({ data: e?.response?.data, status: e?.response?.status })
      );

      if (status !== 200 || !asset?.id) {
        toast.error(asset?.error || `Asset "${tag}" not found`);
        return;
      }

      // Ref-backed check — never stale even if React hasn't re-rendered yet
      if (selectedIdsRef.current.has(asset.id)) {
        toast.info(`${assetDisplayName(asset)} already added`, { icon: 'ℹ️' });
        return;
      }

      // Check availability
      if (asset.status !== 'available') {
        const owner = asset.current_assignee_name || 'another employee';
        toast.error(`${assetDisplayName(asset)} is ${asset.status_display || asset.status} (${owner}) — cannot assign`);
        return;
      }

      setSelectedAssets((prev) => [...prev, asset]);
      setScanSuccess(true);
      setTimeout(() => setScanSuccess(false), 600);
      toast.success(`${assetDisplayName(asset)} added`);
    } catch (e) {
      toast.error('Scan lookup failed');
    } finally {
      // finally always runs after any return — owns ALL cleanup.
      // Do NOT reset busyRef here: the 1500ms timeout is the actual cooldown.
      // Resetting it immediately (the original bug) made the timeout a no-op.
      setScanLookup(false);
      setTimeout(() => {
        busyRef.current = false;
        lastScannedTagRef.current = null;
      }, 1500);
    }
  };

  const closeScannerOnly = async () => {
    await stopScanner();
    setScannerOpen(false);
  };

  const toggleAsset = (asset) => {
    setSelectedAssets((prev) =>
      prev.some((a) => a.id === asset.id)
        ? prev.filter((a) => a.id !== asset.id)
        : [...prev, asset]
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedEmployee || selectedAssets.length === 0) return;
    onSubmit({
      asset_ids: selectedAssets.map((a) => a.id),
      employee_id: selectedEmployee,
      effective_date: getTodayInSingaporeTimezone(),
      notes,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Bulk Assign Assets"
      size="lg"
      theme="admin"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-admin-outline">
            Cancel
          </button>
          <button
            type="submit"
            form="bulk-assign-form"
            disabled={!selectedEmployee || selectedAssets.length === 0 || isLoading}
            className="btn btn-admin-primary"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            {isLoading ? 'Assigning...' : `Assign ${selectedAssets.length} Asset(s)`}
          </button>
        </>
      }
    >
      <form id="bulk-assign-form" onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="form-label">
                  Assign to <span className="text-red-500">*</span>
                </label>
                <AssetAssigneeCombobox
                  value={selectedEmployee}
                  onChange={setSelectedEmployee}
                  theme="admin"
                  disabled={isLoading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Inactive and offboarded people are not listed — they cannot receive new assets.
                </p>
              </div>

              {/* Asset Picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Assets <span className="text-red-500">*</span>
                  <span className="ml-2 text-xs text-gray-500">({selectedAssets.length} selected)</span>
                </label>

                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={assetSearch}
                      onChange={(e) => setAssetSearch(e.target.value)}
                      placeholder="Search available assets by tag, serial, type..."
                      className="input-admin pl-10 w-full"
                    />
                  </div>
                  {!scannerOpen && (
                    <button
                      type="button"
                      onClick={startScanner}
                      className="btn btn-admin-outline w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2 sm:whitespace-nowrap"
                      title="Scan QR codes to add assets"
                      aria-label="Scan"
                    >
                      <ScanLine className="w-4 h-4" />
                      <span className="hidden sm:inline">Scan</span>
                    </button>
                  )}
                </div>

                {/* QR Scanner */}
                {scannerOpen && (
                  <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-red-800 flex items-center gap-1.5">
                        <Camera className="w-4 h-4" />
                        Scanning — point at asset QR codes
                      </p>
                      <button
                        type="button"
                        onClick={closeScannerOnly}
                        className="text-red-600 hover:text-red-800 p-1"
                        aria-label="Close scanner"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div
                      id={BULK_SCANNER_DIV}
                      className={`w-full rounded-lg overflow-hidden bg-gray-900 min-h-[220px] border-2 transition-colors duration-300 ${
                        scanSuccess ? 'border-green-400' : 'border-transparent'
                      }`}
                    />
                    {scanLookup && (
                      <p className="text-xs text-red-700 text-center animate-pulse">Looking up asset…</p>
                    )}
                    {selectedAssets.length > 0 && (
                      <p className="text-xs text-center text-red-700 font-medium">
                        {selectedAssets.length} asset{selectedAssets.length !== 1 ? 's' : ''} scanned so far
                      </p>
                    )}
                  </div>
                )}

                {/* Selected chips — hidden while scanner is open to prevent bleed-through */}
                {selectedAssets.length > 0 && !scannerOpen && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedAssets.map((a) => (
                      <span
                        key={a.id}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium"
                      >
                        {assetDisplayName(a)}
                        <button
                          type="button"
                          onClick={() => toggleAsset(a)}
                          className="hover:text-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Available assets dropdown */}
                {debouncedAssetSearch.trim().length >= 2 && (
                  <div className="mt-1 border rounded-lg max-h-40 overflow-y-auto">
                    {loadingAssets ? (
                      <div className="p-2 text-center text-sm text-gray-500">Searching...</div>
                    ) : availableAssets.length === 0 ? (
                      <div className="p-2 text-center text-sm text-gray-500">No available assets found</div>
                    ) : (
                      availableAssets.map((asset) => (
                        <button
                          key={asset.id}
                          type="button"
                          onClick={() => toggleAsset(asset)}
                          className="w-full text-left px-3 py-2 hover:bg-red-50 text-sm flex items-center justify-between"
                        >
                          <div>
                            <span className="font-medium">{assetDisplayName(asset)}</span>
                            <span className="text-gray-500 ml-2">{asset.asset_type_name || asset.asset_type?.name}</span>
                          </div>
                          {asset.serial_number && (
                            <span className="text-xs text-gray-400">S/N: {asset.serial_number}</span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional notes..."
                  className="input-admin w-full"
                />
              </div>
      </form>
    </Modal>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const AssetAdminAssets = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useTabPersistence('asset-admin-assets', 'all', VALID_TABS);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = usePersistence('asset-admin-assets-viewMode', getDefaultViewMode());
  
  // Filter panel state
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  // Filters are initialized FROM the URL so a refresh / shared link restores
  // the exact view. They are written back to the URL by the effect below.
  //   categories  → ['IT', 'FLT', 'LIC', 'ACC', 'FUEL', 'DSL']
  //   statuses    → ['available', 'assigned', ...]
  //   departments → department IDs
  //   ownershipTypes → ['owned', 'leased']
  const [filters, setFilters] = useState(() => parseFiltersFromParams(searchParams));
  
  // Modal states
  const [assignModal, setAssignModal] = useState({ open: false, asset: null });
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [returnModal, setReturnModal] = useState({ open: false, asset: null });
  const [editModal, setEditModal] = useState({ open: false, asset: null });
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [letterModal, setLetterModal] = useState({ open: false, asset: null });
  const [deactivateModal, setDeactivateModal] = useState({ open: false, asset: null });
  const [lessorModal, setLessorModal] = useState({ open: false, asset: null });
  const { openAssetDetail } = useAssetDetail();

  // Export state
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const exportMenuRef = useRef(null);
  const exportDropdownRef = useRef(null);
  
  // Filter helper functions
  const toggleFilter = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].includes(value)
        ? prev[filterType].filter(v => v !== value)
        : [...prev[filterType], value]
    }));
    setCurrentPage(1); // Reset to page 1 on filter change
  };
  
  const clearAllFilters = () => {
    setFilters({ ...EMPTY_FILTERS });
    setCurrentPage(1);
  };
  
  // Count active filters for badge
  const activeFilterCount = useMemo(() => 
    filters.categories.length
    + filters.statuses.length
    + filters.departments.length
    + (filters.ownershipTypes?.length || 0)
  , [filters]);
  
  // Check if any filters are active
  const hasActiveFilters = activeFilterCount > 0;
  
  // Reset to page 1 when filters change
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setCurrentPage(1);
  };
  
  
  // Data fetching - categories must be loaded first for validation
  const { data: categories = [] } = useAssetCategories();
  const { data: departments = [] } = useDepartments();
  
  // Build query params using utility function (normalized for consistent query keys)
  const queryParams = useMemo(() => {
    return buildAssetQueryParams({
      searchParams,
      filters,
      activeTab,
      searchQuery,
      currentPage,
      categories,
    });
  }, [activeTab, searchQuery, searchParams, currentPage, filters, categories]);
  
  const { data: assetsData, isLoading, error } = useAssets(queryParams);

  // Persist active multi-select filters to the URL (source of truth).
  // This is what makes the filter survive a refresh and lets the view be
  // bookmarked / shared. Uses { replace: true } so filtering doesn't spam the
  // browser history stack. Empty filters are removed to keep URLs clean.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(FILTER_URL_KEYS).forEach(([filterKey, urlKey]) => {
          const values = filters[filterKey];
          if (values && values.length > 0) {
            next.set(urlKey, values.join(','));
          } else {
            next.delete(urlKey);
          }
        });
        return next;
      },
      { replace: true }
    );
  }, [filters, setSearchParams]);

  // Sync filters FROM the URL when it changes externally (browser back/forward,
  // direct navigation to a shared link). The lazy initializer only reads the URL
  // on mount, so this keeps state in step with the address bar afterwards.
  // No-ops when the URL already matches state, so it never loops with the
  // write-back effect above.
  useEffect(() => {
    const urlFilters = parseFiltersFromParams(searchParams);
    const sameList = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);
    setFilters((prev) =>
      sameList(prev.categories, urlFilters.categories) &&
      sameList(prev.statuses, urlFilters.statuses) &&
      sameList(prev.departments, urlFilters.departments)
        ? prev
        : urlFilters
    );
  }, [searchParams]);

  // Normalize deep links and validate codes once categories are loaded.
  // 1. Fold a legacy singular ?category=<id|code> deep link (used by the
  //    dashboard cards) into the canonical multi-select filter.
  // 2. Drop any category codes that don't exist, with user feedback.
  useEffect(() => {
    if (categories.length === 0) return; // wait for categories to load

    const validCodes = new Set(categories.map((c) => c.code));

    // (1) Fold ?category= into filters.categories, then strip the legacy param
    const urlCategory = searchParams.get('category');
    if (urlCategory) {
      let resolvedCode = null;
      if (/^\d+$/.test(urlCategory)) {
        resolvedCode = categories.find((c) => c.id === parseInt(urlCategory, 10))?.code || null;
      } else if (validCodes.has(urlCategory)) {
        resolvedCode = urlCategory;
      }

      if (resolvedCode) {
        setFilters((prev) =>
          prev.categories.includes(resolvedCode)
            ? prev
            : { ...prev, categories: [...prev.categories, resolvedCode] }
        );
      } else {
        toast.error('Invalid category filter. Showing all assets.');
      }

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('category');
          return next;
        },
        { replace: true }
      );
    }

    // (2) Prune invalid category codes that arrived via the URL
    setFilters((prev) => {
      const invalid = prev.categories.filter((code) => !validCodes.has(code));
      if (invalid.length === 0) return prev;
      toast.error(`Invalid category filter(s): ${invalid.join(', ')}. Showing all assets.`);
      return { ...prev, categories: prev.categories.filter((code) => validCodes.has(code)) };
    });
  }, [searchParams, categories, setSearchParams]);

  // Mutations
  const assignAsset = useAssignAsset();
  const bulkAssign = useBulkAssignAssets();
  const returnToInventory = useReturnToInventory();
  const deactivateAccess = useDeactivateAccess();
  const returnToLessor = useReturnToLessor();
  
  const assets = assetsData?.results || assetsData || [];
  const totalCount = assetsData?.count || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  
  // Tab definitions
  const tabs = [
    { id: 'all', name: 'All Assets', icon: Package },
    { id: 'available', name: 'Available', icon: CheckCircle },
    { id: 'assigned', name: 'Assigned', icon: User },
    { id: 'in_repair', name: 'In Repair', icon: AlertTriangle },
    { id: 'pending_disposal', name: 'Pending Disposal', icon: AlertCircle },
  ];
  
  // Handlers
  const handleAssign = (data) => {
    assignAsset.mutate(data, {
      onSuccess: () => setAssignModal({ open: false, asset: null }),
    });
  };

  const handleBulkAssign = (data) => {
    bulkAssign.mutate(data, {
      onSuccess: () => setBulkAssignOpen(false),
    });
  };
  
  const handleReturn = (data) => {
    returnToInventory.mutate(data, {
      onSuccess: () => setReturnModal({ open: false, asset: null }),
    });
  };

  const handleReturnToLessor = (data) => {
    const asset = lessorModal.asset;
    if (!asset) return;
    returnToLessor.mutate(
      { id: asset.id, data },
      { onSuccess: () => setLessorModal({ open: false, asset: null }) },
    );
  };

  const handleDeactivateAccess = () => {
    const asset = deactivateModal.asset;
    if (!asset) return;
    deactivateAccess.mutate(asset.id, {
      onSuccess: () => setDeactivateModal({ open: false, asset: null }),
    });
  };

  // Close export menu on outside click (dropdown is portaled, so check both refs)
  useEffect(() => {
    if (!showExportMenu) return;
    const handleClickOutside = (e) => {
      const inTrigger = exportMenuRef.current?.contains(e.target);
      const inMenu = exportDropdownRef.current?.contains(e.target);
      if (!inTrigger && !inMenu) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  const buildFilterLabels = useCallback(() => {
    const labels = [];
    if (activeTab !== 'all') {
      const tab = tabs.find((t) => t.id === activeTab);
      labels.push(`Tab: ${tab?.name || activeTab}`);
    }
    if (searchQuery) labels.push(`Search: "${searchQuery}"`);
    if (filters.categories.length > 0) {
      const catNames = filters.categories.map(
        (code) => categories.find((c) => c.code === code)?.name || code
      );
      labels.push(`Category: ${catNames.join(', ')}`);
    }
    if (filters.statuses.length > 0) {
      const statusNames = filters.statuses.map(
        (val) => STATUS_OPTIONS.find((s) => s.value === val)?.label || val
      );
      labels.push(`Status: ${statusNames.join(', ')}`);
    }
    if (filters.departments.length > 0) {
      const deptNames = filters.departments.map(
        (id) => departments.find((d) => String(d.id) === id)?.department_name || id
      );
      labels.push(`Dept: ${deptNames.join(', ')}`);
    }
    if (filters.ownershipTypes?.length > 0) {
      const ownershipNames = filters.ownershipTypes.map(
        (val) => OWNERSHIP_OPTIONS.find((o) => o.value === val)?.label || val
      );
      labels.push(`Ownership: ${ownershipNames.join(', ')}`);
    }
    return labels;
  }, [activeTab, searchQuery, filters, categories, departments, tabs]);

  const handleExport = useCallback(async (format) => {
    setShowExportMenu(false);
    setIsExporting(true);
    const loadingToast = toast.loading('Preparing export…');

    try {
      const { page, page_size, ...exportParams } = queryParams;
      const response = await assetInventoryAPI.exportAssets(exportParams);
      const exportedAssets = response.data?.results || response.data || [];

      if (exportedAssets.length === 0) {
        toast.dismiss(loadingToast);
        toast.error('No assets match the current filters');
        return;
      }

      const filterLabels = buildFilterLabels();
      toast.dismiss(loadingToast);

      if (format === 'pdf') {
        await exportAssetsPDF(exportedAssets, filterLabels);
      } else if (format === 'xlsx') {
        await exportAssetsXLSX(exportedAssets, filterLabels);
      } else {
        exportAssetsCSV(exportedAssets, filterLabels);
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Export failed. Please try again.');
      console.error('Export error:', error);
    } finally {
      setIsExporting(false);
    }
  }, [queryParams, buildFilterLabels]);

  return (
    <div className="space-y-6 bg-gray-50 min-h-screen">
      {/* Page Header */}
      <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 mb-1 min-w-0">
              <div className="w-8 h-8 sm:w-9 sm:h-9 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Package className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
              </div>
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Assets Management</h1>
            </div>
            <p className="text-xs sm:text-sm text-gray-500 ml-11 sm:ml-12 truncate">View and manage all company assets</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => setBulkAssignOpen(true)}
              className="btn btn-admin-outline w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2"
              aria-label="Bulk Assign"
            >
              <UserPlus className="w-4 h-4" />
              <span className="hidden sm:inline">Bulk Assign</span>
            </button>
            <button
              type="button"
              onClick={() => setAddModalOpen(true)}
              className="btn btn-admin-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2"
              aria-label="Add Asset"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Add Asset</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4 md:mb-6">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`tab-btn-admin ${isActive ? 'active' : ''}`}
            >
              <Icon className="w-4 h-4" />
              {tab.name}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            {/* Left: Search */}
            <DebouncedSearchInput
              value={searchQuery}
              onSearch={(val) => { setSearchQuery(val); setCurrentPage(1); }}
              placeholder="Search assets by tag, serial, type, staff ID, name or alias..."
              theme="admin"
              className="w-full sm:max-w-xl min-w-0"
            />

            {/* Right: Filter → Export → View (PillToggle rightmost per DS toolbar) */}
            <div className="flex items-center gap-2 flex-shrink-0 self-end sm:self-auto">
              <FilterButton
                theme="admin"
                isOpen={showFilterPanel}
                activeCount={activeFilterCount}
                onClick={() => setShowFilterPanel(v => !v)}
              />
              <div ref={exportMenuRef}>
                <button
                  type="button"
                  className="icon-button-admin"
                  title="Export assets"
                  aria-label="Export assets"
                  aria-expanded={showExportMenu}
                  onClick={() => setShowExportMenu((v) => !v)}
                  disabled={isExporting || totalCount === 0}
                >
                  {isExporting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                </button>

                {showExportMenu && (
                  <ExportDropdownMenu
                    anchorRef={exportMenuRef}
                    dropdownRef={exportDropdownRef}
                    onExport={handleExport}
                  />
                )}
              </div>
              <PillToggle
                value={viewMode}
                onChange={setViewMode}
                options={[
                  { id: 'grid', label: 'Grid view', icon: Grid3X3 },
                  { id: 'list', label: 'List view', icon: List },
                ]}
                variant="icon"
                theme="admin"
                ariaLabel="View mode"
              />
            </div>
          </div>
        </div>
        
        {/* Collapsible Filter Panel */}
        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
          showFilterPanel ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="px-4 pb-4 pt-2 border-t border-gray-100">
            {/* Clear All button at top right */}
            {hasActiveFilters && (
              <div className="flex justify-end mb-3">
                <button
                  onClick={clearAllFilters}
                  className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
                >
                  <X className="w-4 h-4" />
                  Clear All
                </button>
              </div>
            )}
            
            {/* Filter Sections Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Category Filter Section */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Category
                </h4>
                <div className="space-y-1">
                  {categories.length > 0 ? (
                    categories.map(cat => {
                      const Icon = CATEGORY_ICON_MAP[cat.code] || Package;
                      return (
                        <FilterCheckbox
                          key={cat.code}
                          label={cat.name}
                          icon={Icon}
                          checked={filters.categories.includes(cat.code)}
                          onChange={() => toggleFilter('categories', cat.code)}
                        />
                      );
                    })
                  ) : (
                    <p className="text-sm text-gray-400">Loading categories...</p>
                  )}
                </div>
              </div>
              
              {/* Status Filter Section */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Status
                </h4>
                <div className="space-y-1 max-h-48 overflow-y-auto pr-2">
                  {STATUS_OPTIONS.map(status => (
                    <FilterCheckbox
                      key={status.value}
                      label={status.label}
                      checked={filters.statuses.includes(status.value)}
                      onChange={() => toggleFilter('statuses', status.value)}
                    />
                  ))}
                </div>
              </div>
              
              {/* Department Filter Section */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Department
                </h4>
                <div className="space-y-1 max-h-48 overflow-y-auto pr-2">
                  {departments.map(dept => (
                    <FilterCheckbox
                      key={dept.id}
                      label={dept.department_name}
                      checked={filters.departments.includes(String(dept.id))}
                      onChange={() => toggleFilter('departments', String(dept.id))}
                    />
                  ))}
                </div>
              </div>

              {/* Ownership Filter Section */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <FileKey className="w-4 h-4" />
                  Ownership
                </h4>
                <div className="space-y-1 pr-2">
                  {OWNERSHIP_OPTIONS.map((opt) => (
                    <FilterCheckbox
                      key={opt.value}
                      label={opt.label}
                      checked={(filters.ownershipTypes || []).includes(opt.value)}
                      onChange={() => toggleFilter('ownershipTypes', opt.value)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Active Filter Chips (shown below panel when filters applied) */}
        {hasActiveFilters && (
          <div className="px-4 pb-4 border-t border-gray-100 pt-3">
            <div className="flex items-center flex-wrap gap-2">
              <span className="text-xs font-medium text-gray-500">Active:</span>
              
              {/* Category chips */}
              {filters.categories.map(code => {
                const cat = categories.find(c => c.code === code);
                return (
                  <span
                    key={`cat-${code}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-700 text-xs font-medium rounded-full"
                  >
                    {cat?.name || code}
                    <button
                      onClick={() => toggleFilter('categories', code)}
                      className="hover:bg-red-100 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
              
              {/* Status chips */}
              {filters.statuses.map(value => {
                const status = STATUS_OPTIONS.find(s => s.value === value);
                return (
                  <span
                    key={`status-${value}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full"
                  >
                    {status?.label || value}
                    <button
                      onClick={() => toggleFilter('statuses', value)}
                      className="hover:bg-blue-100 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
              
              {/* Department chips */}
              {filters.departments.map(deptId => {
                const dept = departments.find(d => String(d.id) === deptId);
                const dc = getDepartmentColor(dept?.department_name);
                return (
                  <span
                    key={`dept-${deptId}`}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 ${dc.bg} ${dc.text} text-xs font-medium rounded-full`}
                  >
                    {dept?.department_name || deptId}
                    <button
                      onClick={() => toggleFilter('departments', deptId)}
                      className="hover:opacity-70 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}

              {/* Ownership chips */}
              {(filters.ownershipTypes || []).map((value) => {
                const opt = OWNERSHIP_OPTIONS.find((o) => o.value === value);
                return (
                  <span
                    key={`ownership-${value}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-full"
                  >
                    {opt?.label || value}
                    <button
                      onClick={() => toggleFilter('ownershipTypes', value)}
                      className="hover:bg-indigo-100 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
              
              {/* Clear All link */}
              <button
                onClick={clearAllFilters}
                className="text-xs text-gray-500 hover:text-red-600 font-medium ml-2"
              >
                Clear All
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Assets Content */}
      <div className={`${viewMode === 'list' ? 'bg-white rounded-2xl shadow-sm border overflow-hidden' : ''}`}>
        {isLoading ? (
          viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="bg-white rounded-xl border p-4 animate-pulse flex flex-col h-full gap-3">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex-shrink-0"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-5 bg-gray-200 rounded w-24"></div>
                      <div className="h-4 bg-gray-200 rounded w-32"></div>
                    </div>
                    <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                  </div>
                  <div className="ml-[52px] space-y-2 flex-1">
                    <div className="h-3 bg-gray-200 rounded w-28"></div>
                    <div className="h-3 bg-gray-200 rounded w-36"></div>
                  </div>
                  <div className="card-action-footer">
                    <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                    <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                    <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* Mobile skeleton cards */}
              <div className="md:hidden space-y-3 p-4">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 animate-pulse flex flex-col h-full gap-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1">
                        <div className="h-4 bg-gray-200 rounded w-24"></div>
                        <div className="h-3 bg-gray-200 rounded w-32"></div>
                      </div>
                      <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                    </div>
                    <div className="space-y-2 flex-1">
                      <div className="h-3 bg-gray-200 rounded w-full"></div>
                      <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                    </div>
                    <div className="card-action-footer">
                      <div className="w-7 h-7 bg-gray-200 rounded-lg"></div>
                      <div className="w-7 h-7 bg-gray-200 rounded-lg"></div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop skeleton table */}
              <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="table-header border-b">
                  <tr>
                    <th className="table-header-cell">Asset</th>
                    <th className="table-header-cell">Type</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Assigned To</th>
                    <th className="table-header-cell">Next Due</th>
                    <th className="table-header-cell text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                          <div className="space-y-1">
                            <div className="h-4 bg-gray-200 rounded w-24"></div>
                            <div className="h-3 bg-gray-200 rounded w-32"></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <div className="h-4 bg-gray-200 rounded w-20"></div>
                          <div className="h-3 bg-gray-200 rounded w-16"></div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <div className="h-4 bg-gray-200 rounded w-28"></div>
                          <div className="h-3 bg-gray-200 rounded w-16"></div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-gray-200 rounded-full"></div>
                          <div className="space-y-1">
                            <div className="h-4 bg-gray-200 rounded w-20"></div>
                            <div className="h-3 bg-gray-200 rounded w-16"></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                          <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                          <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )
        ) : error ? (
          <div className={`text-center py-12 ${viewMode === 'grid' ? 'bg-white rounded-2xl shadow-sm border' : ''}`}>
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <p className="text-gray-500">Failed to load assets</p>
          </div>
        ) : assets.length === 0 ? (
          <div className={`text-center py-12 ${viewMode === 'grid' ? 'bg-white rounded-2xl shadow-sm border' : ''}`}>
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No assets found</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {assets.map(asset => (
              <AdminAssetCard
                key={asset.id}
                asset={asset}
                onViewDetails={(a) => openAssetDetail(a.id)}
                onAssign={(a) => setAssignModal({ open: true, asset: a })}
                onReturn={(a) => setReturnModal({ open: true, asset: a })}
                onEdit={(a) => setEditModal({ open: true, asset: a })}
                onViewLetter={(a) => setLetterModal({ open: true, asset: a })}
                onReturnToLessor={(a) => setLessorModal({ open: true, asset: a })}
                onDeactivateAccess={(a) => setDeactivateModal({ open: true, asset: a })}
              />
            ))}
          </div>
        ) : (
          <>
            {/* Mobile card list (list view, below md) */}
            <div className="md:hidden space-y-3 p-4">
              {assets.map(asset => {
                const dueDate = asset.next_due_date ? new Date(asset.next_due_date) : null;
                const daysUntilDue = dueDate ? Math.ceil((dueDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
                const dueClass = daysUntilDue === null ? 'text-gray-900'
                  : daysUntilDue < 0 ? 'text-red-600'
                  : daysUntilDue <= 30 ? 'text-amber-600'
                  : daysUntilDue <= 90 ? 'text-yellow-600' : 'text-gray-900';
                return (
                  <div key={asset.id} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col h-full gap-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate flex items-center gap-1.5">
                          <span className="truncate">{assetDisplayName(asset)}</span>
                          {isDeviceMobileLine(asset) && (
                            <InfoBadge color="blue" size="xs" label="Device" title={deviceLineLabel(asset)} />
                          )}
                          <OwnershipBadge ownershipType={asset.ownership_type} />
                          <TrendMicroBadge asset={asset} className="w-3.5 h-3.5 flex-shrink-0" />
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {assetPlanLine(asset) || asset.serial_number || '—'}
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        <StatusBadge status={asset.status} />
                      </div>
                    </div>
                    <div className="space-y-1.5 flex-1">
                      <div className="flex items-start justify-between gap-3 text-sm">
                        <span className="text-gray-500 flex-shrink-0">Type</span>
                        <span className="text-gray-900 text-right truncate min-w-0">
                          {asset.asset_type?.name || asset.asset_type_name}
                        </span>
                      </div>
                      <div className="flex items-start justify-between gap-3 text-sm">
                        <span className="text-gray-500 flex-shrink-0">Assigned To</span>
                        <span className="text-gray-900 text-right truncate min-w-0">
                          {asset.current_assignee_name
                            ? `${asset.current_assignee_name} (${asset.current_assignee_staff_id})`
                            : showsRegisterHolder(asset)
                              ? `${asset.register_holder} (${registerHolderNote(asset)})`
                              : asset.deployed_project_name || '—'}
                        </span>
                      </div>
                      {isDeviceMobileLine(asset) && (
                        <div className="flex items-start justify-between gap-3 text-sm">
                          <span className="text-gray-500 flex-shrink-0">Device</span>
                          <span className="text-blue-700 text-right min-w-0">
                            {deviceLineContext(asset) || 'Site or device'}
                          </span>
                        </div>
                      )}
                      <div className="flex items-start justify-between gap-3 text-sm">
                        <span className="text-gray-500 flex-shrink-0">Next Due</span>
                        <span className={`text-right truncate min-w-0 tabular-nums font-medium ${dueClass}`}>
                          {dueDate
                            ? `${dueDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}${asset.next_due_type ? ` · ${asset.next_due_type}` : ''}`
                            : '—'}
                        </span>
                      </div>
                    </div>
                    <div className="card-action-footer">
                      <button
                        onClick={() => openAssetDetail(asset.id)}
                        className="icon-button-admin"
                        title="View Details"
                        aria-label="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {asset.ack_letter_id && (
                        <button
                          onClick={() => setLetterModal({ open: true, asset })}
                          className="icon-button-admin text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          title="View acknowledgement letter"
                          aria-label="View acknowledgement letter"
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                      )}
                      {canAssignAsset(asset) && (
                        <button
                          onClick={() => setAssignModal({ open: true, asset })}
                          className="icon-button-admin text-green-600 hover:text-green-700 hover:bg-green-50"
                          title="Assign"
                          aria-label="Assign"
                        >
                          <UserPlus className="w-4 h-4" />
                        </button>
                      )}
                      {asset.status === 'assigned' && canReturnAssetToInventory(asset) && (
                        <button
                          onClick={() => setReturnModal({ open: true, asset })}
                          className="icon-button-admin text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
                          title="Return to Inventory"
                          aria-label="Return to Inventory"
                        >
                          <UserMinus className="w-4 h-4" />
                        </button>
                      )}
                      <ReturnToLessorButton
                        asset={asset}
                        onClick={(a) => setLessorModal({ open: true, asset: a })}
                      />
                      {canDeactivateAccess(asset) && (
                        <button
                          onClick={() => setDeactivateModal({ open: true, asset })}
                          className="icon-button-danger"
                          title="Deactivate access"
                          aria-label="Deactivate access"
                        >
                          <ShieldOff className="w-4 h-4" />
                        </button>
                      )}
                      {asset.can_edit !== false && (
                        <button
                          onClick={() => setEditModal({ open: true, asset })}
                          className="icon-button-admin"
                          title="Edit"
                          aria-label="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Desktop table (md and up) */}
            <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="table-header border-b">
                <tr>
                  <th className="table-header-cell">Asset</th>
                  <th className="table-header-cell">Type</th>
                  <th className="table-header-cell">Status</th>
                  <th className="table-header-cell">Assigned To</th>
                  <th className="table-header-cell">Next Due</th>
                  <th className="table-header-cell text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {assets.map(asset => (
                  <tr key={asset.id} className="table-row">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {(() => {
                          const colors = getCategoryColorByCode(asset.category_code);
                          return (
                            <div className={`w-8 h-8 ${colors.bg} rounded-full flex items-center justify-center`}>
                              <CategoryIcon code={asset.category_code} className={`w-4 h-4 ${colors.text}`} />
                            </div>
                          );
                        })()}
                        <div>
                          <p className="font-medium text-gray-900 flex items-center gap-1.5">
                            {assetDisplayName(asset)}
                            {isDeviceMobileLine(asset) && (
                              <InfoBadge color="blue" size="xs" label="Device" title={deviceLineLabel(asset)} />
                            )}
                            <OwnershipBadge ownershipType={asset.ownership_type} />
                            <TrendMicroBadge asset={asset} />
                          </p>
                          <p className="text-xs text-gray-500">{assetPlanLine(asset)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-900">{asset.asset_type?.name || asset.asset_type_name}</p>
                      <p className="text-xs text-gray-500">{asset.category_name}</p>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={asset.status} />
                    </td>
                    <td className="px-4 py-3">
                      {asset.current_assignee_name ? (
                        <div>
                          <p className="text-sm text-gray-900">{asset.current_assignee_name}</p>
                          <p className="text-xs text-gray-500">{asset.current_assignee_staff_id}</p>
                          {isDeviceMobileLine(asset) && (
                            <p className="text-xs text-blue-700 mt-0.5">
                              {deviceLineLabel(asset)}
                            </p>
                          )}
                          {asset.deployed_project_code && (
                            <p className="text-xs text-blue-500 mt-0.5">{asset.deployed_project_code}{asset.deployed_work_site_name ? ` / ${asset.deployed_work_site_name}` : ''}</p>
                          )}
                        </div>
                      ) : showsRegisterHolder(asset) ? (
                        <div>
                          <p className="text-sm text-gray-900">{asset.register_holder}</p>
                          <p className="text-xs text-gray-500">{registerHolderNote(asset)}</p>
                        </div>
                      ) : asset.deployed_project_name ? (
                        <div>
                          <p className="text-sm text-gray-900">{asset.deployed_project_name}</p>
                          <p className="text-xs text-gray-500">{asset.deployed_project_code}{asset.deployed_work_site_name ? ` / ${asset.deployed_work_site_name}` : ''}</p>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {asset.next_due_date ? (
                        (() => {
                          const dueDate = new Date(asset.next_due_date);
                          const today = new Date();
                          const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
                          const isOverdue = daysUntilDue < 0;
                          const isUrgent = daysUntilDue >= 0 && daysUntilDue <= 30;
                          const isWarning = daysUntilDue > 30 && daysUntilDue <= 90;
                          
                          return (
                            <div className="flex items-center gap-2">
                              {isOverdue ? (
                                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                              ) : isUrgent ? (
                                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                              ) : isWarning ? (
                                <Clock className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                              ) : (
                                <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              )}
                              <div>
                                <p className={`text-sm font-medium ${
                                  isOverdue ? 'text-red-600' : 
                                  isUrgent ? 'text-amber-600' : 
                                  isWarning ? 'text-yellow-600' : 'text-gray-900'
                                }`}>
                                  {dueDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                </p>
                                <p className="text-xs text-gray-500">{asset.next_due_type}</p>
                              </div>
                            </div>
                          );
                        })()
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openAssetDetail(asset.id)}
                          className="icon-button-admin"
                          title="View Details"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        {asset.ack_letter_id && (
                          <button
                            onClick={() => setLetterModal({ open: true, asset })}
                            className="icon-button-admin text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            title={`View acknowledgement letter${asset.ack_letter_reference ? ` (${asset.ack_letter_reference})` : ''}`}
                          >
                            <FileText className="w-5 h-5" />
                          </button>
                        )}
                        {canAssignAsset(asset) && (
                          <button
                            onClick={() => setAssignModal({ open: true, asset })}
                            className="icon-button-admin text-green-600 hover:text-green-700 hover:bg-green-50"
                            title="Assign"
                          >
                            <UserPlus className="w-5 h-5" />
                          </button>
                        )}
                        {asset.status === 'assigned' && canReturnAssetToInventory(asset) && (
                          <button
                            onClick={() => setReturnModal({ open: true, asset })}
                            className="icon-button-admin text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
                            title="Return to Inventory"
                          >
                            <UserMinus className="w-5 h-5" />
                          </button>
                        )}
                        <ReturnToLessorButton
                          asset={asset}
                          onClick={(a) => setLessorModal({ open: true, asset: a })}
                          iconClassName="w-5 h-5"
                        />
                        {canDeactivateAccess(asset) && (
                          <button
                            onClick={() => setDeactivateModal({ open: true, asset })}
                            className="icon-button-danger"
                            title="Deactivate access"
                            aria-label="Deactivate access"
                          >
                            <ShieldOff className="w-5 h-5" />
                          </button>
                        )}
                        {asset.can_edit !== false && (
                          <button
                            onClick={() => setEditModal({ open: true, asset })}
                            className="icon-button-admin"
                            title="Edit"
                            aria-label="Edit"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </>
        )}
        
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalCount={totalCount}
          pageSize={PAGE_SIZE}
          theme="admin"
        />
      </div>

      {/* Modals */}
      <AssignModal
        isOpen={assignModal.open}
        onClose={() => setAssignModal({ open: false, asset: null })}
        asset={assignModal.asset}
        onSubmit={handleAssign}
        isLoading={assignAsset.isPending}
      />

      <DeactivateAccessModal
        isOpen={deactivateModal.open}
        onClose={() => setDeactivateModal({ open: false, asset: null })}
        asset={deactivateModal.asset}
        onConfirm={handleDeactivateAccess}
        isLoading={deactivateAccess.isPending}
      />

      <ReturnToLessorModal
        isOpen={lessorModal.open}
        onClose={() => setLessorModal({ open: false, asset: null })}
        asset={lessorModal.asset}
        onSubmit={handleReturnToLessor}
        isLoading={returnToLessor.isPending}
      />
      
      <BulkAssignModal
        isOpen={bulkAssignOpen}
        onClose={() => setBulkAssignOpen(false)}
        onSubmit={handleBulkAssign}
        isLoading={bulkAssign.isPending}
      />

      <ReturnToInventoryModal
        isOpen={returnModal.open}
        onClose={() => setReturnModal({ open: false, asset: null })}
        asset={returnModal.asset}
        onSubmit={handleReturn}
        isLoading={returnToInventory.isPending}
      />

      <EditAssetModal
        isOpen={editModal.open}
        onClose={() => setEditModal({ open: false, asset: null })}
        asset={editModal.asset}
      />

      <AddAssetModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={(res) => {
          const id = res?.data?.id;
          if (id) openAssetDetail(id);
        }}
      />

      {letterModal.open && letterModal.asset?.ack_letter_id && (
        <LetterPreviewModal
          letterId={letterModal.asset.ack_letter_id}
          letterReference={letterModal.asset.ack_letter_reference}
          assetTag={letterModal.asset.asset_tag}
          onClose={() => setLetterModal({ open: false, asset: null })}
          theme="admin"
          allLettersHref={null}
        />
      )}
    </div>
  );
};

export default AssetAdminAssets;
